import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { billingService } from '../services/billingService';

const SuccessPage = () => {
  const navigate = useNavigate();
  const { loadCredits, refreshUserProfile } = useAuth();
  const toast = useToast();
  const [syncStatus, setSyncStatus] = useState('syncing');

  useEffect(() => {
    const syncPayment = async () => {
      const params = new URLSearchParams(window.location.search);
      const sessionId = params.get('session_id');
      const mode = params.get('mode'); // 'subscription' ou 'credits'

      try {
        if (sessionId) {
          // Synchroniser via session_id (fonctionne pour credits ET subscriptions)
          console.log('[SuccessPage] Syncing checkout session:', sessionId);
          await billingService.syncCheckoutSession(sessionId);
        } else if (mode === 'subscription') {
          // Fallback: sync subscription manuellement
          console.log('[SuccessPage] No session_id, trying subscription sync');
          await billingService.syncSubscription?.();
        }
        
        // Rafraîchir les crédits ET le profil utilisateur (pour le plan)
        await Promise.all([
          loadCredits(),
          refreshUserProfile?.(),
        ]);
        
        setSyncStatus('success');
        console.log('[SuccessPage] Sync completed successfully');

        // Toast de confirmation selon le type de paiement
        if (mode === 'subscription') {
          toast.success('🎉 Bienvenue sur Muzo+ ! Votre abonnement est maintenant actif.', {
            duration: 6000,
            title: 'Abonnement activé',
          });
        } else {
          toast.success('Vos crédits ont été ajoutés à votre compte !', {
            duration: 5000,
            title: 'Paiement réussi',
          });
        }
      } catch (err) {
        console.error('[SuccessPage] Sync failed:', err);
        setSyncStatus('error');
        // Quand même essayer de rafraîchir les données
        await loadCredits().catch(() => {});
        await refreshUserProfile?.().catch(() => {});
        
        toast.warning('Paiement reçu. Vos crédits seront ajoutés sous peu.', {
          duration: 5000,
        });
      }

      // Rediriger vers le dashboard après un court délai
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    };

    // Attendre un peu pour laisser le webhook Stripe traiter
    const timer = setTimeout(syncPayment, 1500);
    return () => clearTimeout(timer);
  }, [loadCredits, refreshUserProfile, navigate, toast]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-slate-100 dark:from-blue-900 dark:via-gray-900 dark:to-gray-800 px-4">
      <div className="bg-white text-slate-900 dark:text-white dark:bg-gray-900 rounded-2xl shadow-2xl p-8 w-full max-w-md flex flex-col items-center border border-slate-200 dark:border-white/10">
        {syncStatus === 'syncing' ? (
          <>
            <svg className="w-20 h-20 text-indigo-500 dark:text-indigo-400 mb-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <h1 className="text-2xl font-bold text-indigo-600 dark:text-indigo-400 mb-2 text-center">
              Synchronisation en cours...
            </h1>
            <p className="text-slate-600 dark:text-gray-300 text-center">
              Veuillez patienter pendant que nous activons votre achat.
            </p>
          </>
        ) : (
          <>
            <svg className="w-20 h-20 text-green-500 dark:text-green-400 mb-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12l2 2 4-4" />
            </svg>
            <h1 className="text-3xl font-extrabold text-green-600 dark:text-green-400 mb-2 text-center">
              {syncStatus === 'success' ? 'Paiement validé !' : 'Paiement traité'}
            </h1>
            <p className="text-slate-600 dark:text-gray-300 text-center mb-6">
              {syncStatus === 'success' 
                ? 'Merci pour votre achat ! Vos crédits ont été ajoutés à votre compte.'
                : 'Votre paiement a été reçu. Si vos crédits ne s\'affichent pas immédiatement, ils seront ajoutés sous peu.'
              }
              <br />
              Vous allez être redirigé vers le tableau de bord...
            </p>
            <button
              className="mt-4 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg transition"
              onClick={() => navigate('/dashboard')}
            >
              Retour au tableau de bord
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default SuccessPage;
